import {
	canUserRequestAccess,
	isSite7DaysOld,
	hasAccessToProduct,
	isSiteOptedOut,
	isUserTooNew,
} from '@atlassian/cross-join-nudges';

import { withEligibilityCache } from './caching.ts';
import { getFeatureVisitedCount, isUserOptedOut } from './feature-visited.ts';

export type JpdIneligibilityReason =
	| 'NEW_SITE_LESS_THAN_7_DAYS_OLD'
	| 'ALREADY_HAVE_ACCESS'
	| 'NOT_ELIGIBLE_FOR_REQUEST_ACCESS'
	| 'SITE_OPTED_OUT'
	| 'USER_IS_TOO_NEW'
	| 'FEATURE_VISITED_COUNT_MAXED_OUT'
	| 'USER_HAS_OPTED_OUT';

export type JpdResolverResultResult =
	| {
			eligible: false;
			ineligibilityReason: JpdIneligibilityReason;
	  }
	| { eligible: true };

const MAX_FEATURE_VISIT_COUNT = 3;
export const CONF_TO_JPD_INTEGRATION = 'crossJoinNudgesConfluenceToJpd';

export const resolveJpdCrossJoinNudges = async (
	cloudId: string,
	userId: string,
): Promise<JpdResolverResultResult> => {
	const cachedUserIsTooNew = withEligibilityCache('user_is_too_new', isUserTooNew, 1);

	if (getFeatureVisitedCount(CONF_TO_JPD_INTEGRATION) >= MAX_FEATURE_VISIT_COUNT) {
		return {
			eligible: false,
			ineligibilityReason: 'FEATURE_VISITED_COUNT_MAXED_OUT',
		};
	}

	if (isUserOptedOut(CONF_TO_JPD_INTEGRATION)) {
		return {
			eligible: false,
			ineligibilityReason: 'USER_HAS_OPTED_OUT',
		};
	}

	if (await isSiteOptedOut(cloudId)) {
		return {
			eligible: false,
			ineligibilityReason: 'SITE_OPTED_OUT',
		};
	}

	if (await hasAccessToProduct('jira-product-discovery', cloudId)) {
		return {
			eligible: false,
			ineligibilityReason: 'ALREADY_HAVE_ACCESS',
		};
	}

	if (await cachedUserIsTooNew(cloudId, userId, 'confluence_first_active')) {
		return { eligible: false, ineligibilityReason: 'USER_IS_TOO_NEW' };
	}

	if (!(await isSite7DaysOld('jira-product-discovery', cloudId))) {
		return {
			eligible: false,
			ineligibilityReason: 'NEW_SITE_LESS_THAN_7_DAYS_OLD',
		};
	}

	if (!(await canUserRequestAccess('jira-product-discovery', cloudId))) {
		return {
			eligible: false,
			ineligibilityReason: 'NOT_ELIGIBLE_FOR_REQUEST_ACCESS',
		};
	}

	return {
		eligible: true,
	};
};
